// browser polyfills
import React, { useCallback, useState } from 'react';
import '@babel/polyfill';
import 'whatwg-fetch';
import { Layout, Result } from '@nackle/paper';
import NackleMenu from '@nackle/menu';
import { Route, Link, routeHistory } from '@nackle/biw-web-router';
import { routes, bundleRoutingMiddleware, redirectMiddleware } from './app-routing';
import { Bridge } from '@nackle/bridge';
import auth from './app-auth';
import {
    AppHeader,
    AppFooter,
    AppError,
    CMXService,
    applyRequestMiddleware,
    authRequestMiddleware,
    authRouteMiddleware
} from '@nackle/studio-tools';
import { setLanguageBundle } from '@nackle/intl-tools';
import { merge } from 'lodash';
import { localeCode as forcedLocaleCode, requiredCmxProjectNames, getLocalLanguageBundle } from './translations';
import '@nackle/studio-tools/dist/studio-tools.css';
import './app.less';

const { Content } = Layout;

export default function App() {

    const [ loaded, setLoaded ] = useState( false );
    const [ cmxBundleLoaded, setCMXBundleLoaded ] = useState( false );
    const [ accessToken, setAccessToken ] = useState( '' );

    const fetchTranslations = useCallback( async () => {
        if ( auth.state ) {
            // Prioritize whatever locale code is forced by the studio since we probably aren't ever going
            // to actually translate the studio. Fallback to user locale code.
            const localeCode = forcedLocaleCode || auth.state.claims.locale;

            const cmxService = new CMXService( auth.state );
            const fetchBundle = async ( projectName, localeCode ) => {
                const cmxBundle = await cmxService.getBundle( [ projectName ], localeCode ).catch( () => null );
                return ( cmxBundle ) ? cmxBundle : getLocalLanguageBundle( projectName, localeCode );
            };

            // Fetch required translation bundles from CMX (or local files if needed).
            const bundles = await Promise.all( requiredCmxProjectNames.map( ( projectName ) => fetchBundle( projectName ) ) );

            // Combine all fetched bundles and load them into intl-tools to be used by the components.
            await setLanguageBundle( localeCode, merge( ...bundles ) );
        } else {
            console.error( 'Unable to fetch translations with missing or invalid auth state.', auth.state );
        }
    }, [] );

    const handleSignIn = async () => {
        await routeHistory.refresh();
        await fetchTranslations();
        
        setLoaded( true );
        setCMXBundleLoaded( true );
        setAccessToken( auth.state.access_token );
    };
    
    const handleCompanyChange = ( company ) => {
        routeHistory.push( `/${ company.id }` );
    };

    return (
        <AppError>
            <Route render={ ( route ) => {
                // set the title
                document.title = route && route.match ? route.match.title : 'Home';
                
                const companyId = route.state?.params?.companyId;
                
                return (
                    <Bridge
                        auth={ auth }
                        onSignIn={ handleSignIn }
                    >
                        
                        { loaded &&
                            <Layout className="root-layout">
                                <AppHeader
                                    auth={ auth }
                                    companyId={ companyId }
                                    onCompanyChange={ handleCompanyChange }
                                    companySelectDisabled={ true }
                                />
                                <Layout>
                                    <NackleMenu
                                        auth={ auth }
                                        cmxBundleLoaded={ cmxBundleLoaded }
                                    />
                                    <Layout className="page-layout">
                                        <Content className="app-content">
                                            <div className="scrollable-content">
                                                <div className="app-content-body">
                                                    {
                                                        route.match ?
                                                            React.cloneElement( route.component, { companyId, accessToken } )
                                                            : (
                                                                <Result
                                                                    status="404"
                                                                    title="404"
                                                                    subTitle="Sorry, the page you visited does not exist."
                                                                    extra={ <Link to="/" className="ant-btn ant-btn-primary">Back Home</Link> }
                                                                />
                                                            )
                                                    }
                                                </div>
                                                <AppFooter className="app-content-footer" />
                                            </div>
                                        </Content>
                                    </Layout>
                                </Layout>
                            </Layout>
                        }
                    
                    </Bridge>
                );
            } } />
        </AppError>
    );
}

// connect routing
routeHistory.applyRoutes( routes );
routeHistory.applyMiddleware( [ authRouteMiddleware( auth ), bundleRoutingMiddleware, ...redirectMiddleware ] );

// connect Auth to Fetch
applyRequestMiddleware( authRequestMiddleware( auth ) );
